import React, { useEffect, useState } from "react";

export type InvoicePreviewZoom = 25 | 50 | 75 | 100 | "fit";

type InvoicePreviewZoomBarProps = {
  zoom: InvoicePreviewZoom;
  onZoomChange: (zoom: InvoicePreviewZoom) => void;
};

export const InvoicePreviewZoomBar = ({
  zoom,
  onZoomChange,
}: InvoicePreviewZoomBarProps) => (
  <div className="flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
    {([25, 50, 75, 100] as const).map((value) => (
      <button
        aria-pressed={zoom === value}
        className={`rounded-md px-3 py-2 text-[13px] transition ${
          zoom === value
            ? "bg-[#E1A731] text-white"
            : "bg-transparent text-gray-800 hover:bg-gray-100"
        }`}
        key={value}
        onClick={() => onZoomChange(value)}
        type="button"
      >
        {value}%
      </button>
    ))}
    <button
      aria-pressed={zoom === "fit"}
      className={`rounded-md px-3 py-2 text-[13px] transition ${
        zoom === "fit"
          ? "bg-[#E1A731] text-white"
          : "bg-transparent text-gray-800 hover:bg-gray-100"
      }`}
      onClick={() => onZoomChange("fit")}
      type="button"
    >
      Fit on page
    </button>
  </div>
);

type InvoicePreviewFitScaleInput = {
  open: boolean;
  pageWidth: number;
  viewportRef: React.RefObject<HTMLDivElement>;
  pageRef: React.RefObject<HTMLDivElement>;
  // Callers memoize the content dependencies so zoom changes keep the current fit.
  deps: React.DependencyList;
  // False fits the page width only: for a stack of several invoices that the
  // user scrolls (Create Member, Monday 13211322856).
  fitHeight?: boolean;
};

export const getInvoicePreviewFitScale = ({
  availableHeight,
  fitHeight,
  measuredPageHeight,
  pageWidth,
  viewportWidth,
}: {
  availableHeight: number;
  fitHeight: boolean;
  measuredPageHeight: number;
  pageWidth: number;
  viewportWidth: number;
}): number =>
  Math.max(
    0.1,
    Math.min(
      1,
      viewportWidth / pageWidth,
      fitHeight && measuredPageHeight > 0
        ? availableHeight / measuredPageHeight
        : 1,
    ),
  );

type InvoicePreviewFitScale = {
  fitScale: number;
  pageHeight: number;
};

export const useInvoicePreviewFitScale = ({
  open,
  pageWidth,
  viewportRef,
  pageRef,
  deps,
  fitHeight = true,
}: InvoicePreviewFitScaleInput): InvoicePreviewFitScale => {
  const [fitScale, setFitScale] = useState(0.5);
  const [pageHeight, setPageHeight] = useState(0);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const calculateFit = () => {
      const viewport = viewportRef.current;
      const page = pageRef.current;

      if (!viewport || !page) {
        return;
      }

      const measuredPageHeight = page.offsetHeight;
      const viewportRect = viewport.getBoundingClientRect();
      const availableHeight = Math.max(
        200,
        window.innerHeight - viewportRect.top - 24,
      );
      setPageHeight(measuredPageHeight);
      setFitScale(getInvoicePreviewFitScale({
        availableHeight,
        fitHeight,
        measuredPageHeight,
        pageWidth,
        viewportWidth: viewport.clientWidth,
      }));
    };

    calculateFit();
    const frameId = window.requestAnimationFrame(calculateFit);
    window.addEventListener("resize", calculateFit);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(calculateFit);

    if (pageRef.current) {
      resizeObserver?.observe(pageRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", calculateFit);
      resizeObserver?.disconnect();
    };
  }, [deps, fitHeight, open, pageRef, pageWidth, viewportRef]);

  return { fitScale, pageHeight };
};

type InvoicePreviewPrintStylesProps = {
  paperSelector?: string;
  pageSelector?: string;
};

// Invoice paper geometry: the design renders the invoice at 1070px. US Letter
// with 0.4in margins leaves 7.7in (739px at 96 CSS px per inch), so print
// zooms the unchanged desktop layout to fit one page width.
export const invoicePrintPaperWidthPx = 1070;
export const invoicePrintZoom =
  Math.floor(((7.7 * 96) / invoicePrintPaperWidthPx) * 1000) / 1000;

export const getInvoicePreviewPrintCss = ({
  paperSelector = "[data-invoice-paper-root]",
  pageSelector = "[data-invoice-preview-page]",
}: InvoicePreviewPrintStylesProps = {}): string => `
    @page {
      size: letter;
      margin: 0.4in;
    }

    @media print {
      html,
      body {
        background: #fff !important;
        height: auto !important;
        overflow: visible !important;
      }

      /* Remove every branch that does not contain the invoice paper, so the
         app, the preview toolbar, and dialog backdrops take no page space. */
      body *:not(:has(${paperSelector})):not(${paperSelector}):not(${paperSelector} *) {
        display: none !important;
      }

      /* Ancestors of the paper (dialog root, container, scroll viewport,
         scaled preview wrapper) print as plain static blocks. */
      body *:has(${paperSelector}) {
        background: transparent !important;
        box-shadow: none !important;
        height: auto !important;
        inset: auto !important;
        margin: 0 !important;
        max-height: none !important;
        max-width: none !important;
        min-height: 0 !important;
        overflow: visible !important;
        padding: 0 !important;
        position: static !important;
        transform: none !important;
        width: auto !important;
      }

      ${pageSelector} {
        transform: none !important;
        width: auto !important;
      }

      ${paperSelector} {
        border-radius: 0 !important;
        box-shadow: none !important;
        margin: 0 !important;
        max-width: none !important;
        position: relative !important;
        width: ${invoicePrintPaperWidthPx}px !important;
        zoom: ${invoicePrintZoom};
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      ${paperSelector} [data-invoice-print-hide] {
        display: none !important;
      }

      ${paperSelector} [data-invoice-print-keep] {
        break-inside: avoid;
        page-break-inside: avoid;
      }
    }
  `;

export const InvoicePreviewPrintStyles = ({
  paperSelector,
  pageSelector,
}: InvoicePreviewPrintStylesProps): React.ReactElement => (
  <style>{getInvoicePreviewPrintCss({ pageSelector, paperSelector })}</style>
);
